import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link as MLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { guideTourAccommodationInfo } from '../api';
import GuideTourAccommodationRoomGroupDialog from '../components/GuideTourAccommodationRoomGroupDialog';
import { HotelCard } from '../components/HotelCard';
import { AuthContext } from '../context/AuthContext';
import { IGuideSalesIndividualTourAccommodationInfo } from '../utils/types';
import { HomeTwoTone } from '@mui/icons-material';

export function GuidanceTourAccommodationPage() {
  const { t } = useTranslation();
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<
    IGuideSalesIndividualTourAccommodationInfo[]
  >([]);
  const [selectedTourPlanId, setSelectedTourPlanId] = useState<number | null>(
    null,
  );
  const { salesIndividualId } = useParams();
  const [searchParams] = useSearchParams();
  const tourId = searchParams.get('tourId');

  useEffect(() => {
    fetchGuideTourAccommodationInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGuideTourAccommodationInfo = async () => {
    if (!salesIndividualId) return;
    try {
      const data = await guideTourAccommodationInfo(salesIndividualId, token);
      setData(data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.message || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setSelectedTourPlanId(null);
  };

  const handleDialogOpen = (tourPlanId: number) => {
    setSelectedTourPlanId(tourPlanId);
  };

  if (!salesIndividualId) return null;

  return (
    <Box sx={{ maxWidth: 'lg' }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        {t('GuidanceTourAccommodationPage.title')}
      </Typography>

      <Breadcrumbs sx={{ mb: 2 }}>
        <MLink
          component={Link}
          to="/"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeTwoTone />
        </MLink>
        <MLink component={Link} to="/account">
          {t('nav.account')}
        </MLink>
        <MLink to="/account/guidance" component={Link}>
          <Typography>{t('nav.guidance')}</Typography>
        </MLink>
        <MLink
          component={Link}
          to={`/account/guidance/${salesIndividualId}?tourId=${tourId}`}
        >
          {t('GuidanceTourAccommodationPage.tour', { tourId })}
        </MLink>
        <Typography>
          {t('GuidanceTourAccommodationPage.accommodation')}
        </Typography>
      </Breadcrumbs>

      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('GuidanceTourAccommodationPage.loading')}
          </Typography>
        </Box>
      )}

      {data.map((acc) => (
        <Box key={acc.tourPlanId} sx={{ mb: 4, maxWidth: 'lg' }}>
          <Typography component="h3" variant="h5" sx={{ mb: 1 }}>
            {acc.city}
          </Typography>
          <Box sx={{ maxWidth: 'sm' }}>
            <HotelCard
              accommodationStayId={acc.accommodationStayId}
              city={acc.city}
              startDate={acc.startDate}
              endDate={acc.endDate}
              stayDays={acc.stayNight + 1}
              stayNight={acc.stayNight}
              imageUrl={acc.stayFullPathImage}
              name={acc.stayName}
              stars={acc.starNumber}
              latitude={acc.latitude}
              longitude={acc.longitude}
            />
          </Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1, mt: 2 }}
          >
            <Typography variant="h6">
              {t('GuidanceTourAccommodationPage.rooming')}
            </Typography>
            {acc.unSettledPax === 0 && (
              <Button
                size="small"
                variant="contained"
                onClick={() => handleDialogOpen(acc.tourPlanId)}
              >
                {t('GuidanceTourAccommodationPage.roomGroupList')}
              </Button>
            )}
          </Stack>
          {acc.unSettledPax > 0 && (
            <Alert
              severity="error"
              sx={{ alignItems: 'center', mb: 1 }}
              action={
                <Button
                  color="inherit"
                  onClick={() => handleDialogOpen(acc.tourPlanId)}
                >
                  {t('GuidanceTourAccommodationPage.details')}
                </Button>
              }
            >
              {t('GuidanceTourAccommodationPage.unsettledPax', {
                pax: acc.unSettledPax,
              })}
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableCell>{t('GuidanceTourAccommodationPage.city')}</TableCell>
                <TableCell>
                  {t('GuidanceTourAccommodationPage.hotel')}
                </TableCell>
                <TableCell>
                  {t('GuidanceTourAccommodationPage.roomType')}
                </TableCell>
                <TableCell>
                  {t('GuidanceTourAccommodationPage.bedType')}
                </TableCell>
                <TableCell>
                  {t('GuidanceTourAccommodationPage.totalRooms')}
                </TableCell>
                <TableCell>
                  {t('GuidanceTourAccommodationPage.reservedRooms')}
                </TableCell>
                <TableCell>
                  {t('GuidanceTourAccommodationPage.unsettedRooms')}
                </TableCell>
              </TableHead>
              <TableBody>
                {acc.roomInfos.map((info) => (
                  <TableRow key={`${acc.tourPlanId} ${info.roomType}`}>
                    <TableCell>{acc.city}</TableCell>
                    <TableCell>{acc.stayName}</TableCell>
                    <TableCell>{info.roomType}</TableCell>
                    <TableCell>{info.bedType}</TableCell>
                    <TableCell>{info.settled + info.unSettled}</TableCell>
                    <TableCell>{info.settled}</TableCell>
                    <TableCell>{info.unSettled}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                    {t('GuidanceTourAccommodationPage.total')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {acc.roomInfos.reduce(
                      (acc, cur) => acc + cur.settled + cur.unSettled,
                      0,
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {acc.roomInfos.reduce((acc, cur) => acc + cur.settled, 0)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {acc.roomInfos.reduce((acc, cur) => acc + cur.unSettled, 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      {selectedTourPlanId && (
        <GuideTourAccommodationRoomGroupDialog
          open={selectedTourPlanId !== null}
          salesIndividualId={salesIndividualId}
          tourPlanId={selectedTourPlanId}
          cityName={
            data.find((d) => d.tourPlanId === selectedTourPlanId)?.city || ''
          }
          onClose={handleDialogClose}
        />
      )}
    </Box>
  );
}
